import React, { useState, useEffect } from 'react'
import { string, func, bool, oneOfType, arrayOf, object } from 'prop-types'
import Div from '@ui/layout/Div'
import ImageDropzone from '@ui/elements/ImageDropzone'
import Input from '@ui/inputs/InputType'
import { upload } from '@util/xhrAPI'
import ImageUploadedDropzone from '@ui/elements/ImageUploadedDropzone'

/**
 * Dropzone input for images
 * @param {string} name
 * @param {string} value
 * @param {string} cssClass
 * @param {function} onChange
 * @param {string} targetUrl
 * @param {boolean} isActive
 */
const Dropzone = ({
  name,
  value,
  cssClass,
  onChange,
  targetUrl,
  alt,
  removeOldUploadedFileCallback,
  isActive = true,
  initialize = false,
  avatar = false,
  clearState = false
}) => {
  const dropzoneDataObject = {
    images: [],
    nonTreatedImages: [],
    authorizedIndex: null,
    uploadStarted: false
  }
  const [dropzoneData, setDropzoneData] = useState(dropzoneDataObject)
  const filesInputRef = React.createRef()

  useEffect(() => {
    if (clearState) {
      setDropzoneData(dropzoneDataObject)
    }
  }, [clearState])

  /**
   * Change the file(s) temporary name sent by the server and will be user when treat the form validation on the server side
   * @param {string} response
   */
  const changeUploadedList = file => {
    if (file) {
      value ? value += ',' + file : value = file

      onChange(name, value)
    }
  }

  /**
   * Update the list of treated images and the waiting list by changing the authorizedindex variable so the Imagedropzone proceeed to the upload
   * Takeoff the treated images of the non treated list
   * upadte the serverId of treated file
   * @param {string} response
   * @param {object} image
   * @param {number} index
   */
  const iterateUpload = (response, image, index) => {
    const newDropzoneData = { ...dropzoneData }
    const newImages = [...newDropzoneData.images]
    image.serverId = response
    newImages[index] = image
    if (response) {
      newDropzoneData.nonTreatedImages.splice(0, 1)
      const nonTreatedList = [...newDropzoneData.nonTreatedImages]
      nonTreatedList && nonTreatedList.length ? newDropzoneData.nonTreatedImages = [...nonTreatedList] : newDropzoneData.nonTreatedImages = []
      newDropzoneData.images = [...newImages]
      newDropzoneData.uploadStarted = false
      if (newDropzoneData.nonTreatedImages.length) {
        newDropzoneData.authorizedIndex = newDropzoneData.images.indexOf(newDropzoneData.nonTreatedImages[0])
      } else {
        newDropzoneData.authorizedIndex = null
      }
    }
    setDropzoneData({ ...newDropzoneData })
  }

  /**
   * Proceed to the upload
   * @param {object} image
   * @param {number} index
   * @param {function} progressCallback
   * @param {function} setTmpName
   */
  const uploadFile = async (image, index, progressCallback, setTmpName) => {
    setDropzoneData({ ...dropzoneData, ...{ uploadStarted: true } })
    await upload(targetUrl, image.file, response => {
      if (Array.isArray(response.data)) {
        response.data.forEach(item => {
          triggerStateUpdate(item, image, index, setTmpName)
        })
      } else {
        triggerStateUpdate(response, image, index, setTmpName)
      }
    }, progressCallback)
  }

  /**
   *
   * @param {string} response
   * @param {object} image
   * @param {number} index
   * @param {function} setTmpName
   */
  const triggerStateUpdate = (response, image, index, setTmpName) => {
    changeUploadedList(response)
    setTmpName(response)
    iterateUpload(response, image, index)
  }

  /**
   * Remove temporary file
   * @param {string} tmpName
   */
  const removeFileTMPFromState = tmpName => {
    const data = value.split(',')
    value = data.filter(filter => filter !== tmpName)
    onChange(name, value.join())
  }

  /**
   * Remove file
   * @param {event} e
   * @param {Image} file
   */
  const removeFile = (e, file) => {
    const images = dropzoneData.images.filter(image => image.serverId !== file.serverId)
    removeFileTMPFromState(file.serverId)
    setDropzoneData({ ...dropzoneData, images })
  }

  /**
   * Create images preview
   */
  const makePreviews = () => {
    if (dropzoneData.images.length) {
      return dropzoneData.images.map(function (image, index) {
        return <ImageDropzone
          key={`image-preview-${index}`}
          uploadCallback={uploadFile}
          deleteCallback={removeFile}
          image={image}
          index={index}
          uploadStarted={dropzoneData.uploadStarted}
          authorizedIndex={dropzoneData.authorizedIndex}
          alt={alt}
        />
      })
    } else if (typeof value === 'string' && value !== '') {
      return value.split(',').map(uploadedImage => {
        return <ImageUploadedDropzone
          file={uploadedImage}
          key={`image-preview-${cssClass}`}
          cssFigureClass='image-uploaded-dropzone'
          deleteCallback={removeOldUploadedFileCallback}
        />
      })
    }
  }

  /**
   * create array of Filelist array
   * @param {Filelist} imagesList
   */
  const updateImagesList = imagesList => {
    if (imagesList && imagesList.length) {
      const treatedList = []
      for (let i = 0; i < imagesList.length; i++) {
        treatedList.push({ file: imagesList[i], serverId: null })
      }
      setDropzoneData({ ...dropzoneData, ...{ images: [...dropzoneData.images, ...treatedList], nonTreatedImages: [...treatedList] } })
    }
  }

  /**
   * initialize the authorizedIndex after first drop or select of images
   */
  useEffect(() => {
    if (dropzoneData.nonTreatedImages.length && !dropzoneData.authorizedIndex) {
      setDropzoneData({ ...dropzoneData, ...{ authorizedIndex: dropzoneData.images.indexOf(dropzoneData.nonTreatedImages[0]) } })
    }
  }, [dropzoneData.nonTreatedImages])

  /**
   * To clear the images list
   */
  useEffect(() => {
    if (initialize && !value && !dropzoneData.nonTreatedImages.length) {
      setDropzoneData({ ...dropzoneDataObject })
    }
  }, [initialize])

  const handleDragEnter = e => {
    e.preventDefault()
    e.stopPropagation()
  }
  const handleDragLeave = e => {
    e.preventDefault()
    e.stopPropagation()
  }
  const handleDragOver = e => {
    e.preventDefault()
    e.stopPropagation()
  }
  const handleDrop = e => {
    e.preventDefault()
    e.stopPropagation()
    updateImagesList(e.dataTransfer.files)
  }

  /**
   * Get list of dragged files
   * @param {event} e
   */
  const getUploadedFiles = () => {
    updateImagesList(filesInputRef.current.files)
  }

  /**
   * Trigger the input file type to popin the fileselct
   * @param {event} e
   */
  const handleClick = () => {
    filesInputRef.current.click()
  }

  /**
   * Create list of actions for tha .dropzone element
   */
  const getRootProps = () => {
    const actions = {
      onDrop: e => handleDrop(e),
      onDragOver: e => handleDragOver(e),
      onDragEnter: e => handleDragEnter(e),
      onDragLeave: e => handleDragLeave(e)
    }
    if (avatar) {
      actions.onClick = e => handleClick(e)
    }
    return actions
  }

  return (
    <Div cssClass={`dropzone ${cssClass} ${isActive ? 'active' : ''} ${value ? 'withPictures' : 'empty'}`} otherProps={{ ...getRootProps() }}>
      <Div cssClass='dropZonePreview'>
        {makePreviews()}
      </Div>
      <Div cssClass='actionZone' otherProps={!avatar ? { onClick: e => handleClick(e) } : {}}>
        Select or drop your pictures here
        <Input cssClass='is-hidden hiddenDropZoneFiles' type='file' onChange={getUploadedFiles} name='dropZoneFiles[]'
          otherProps={{ multiple: true, accept: 'image/*', ref: filesInputRef }} />
      </Div>
    </Div >
  )
}

Dropzone.propTypes = {
  alt: string,
  avatar: bool,
  name: string,
  value: oneOfType([string, bool, arrayOf(object)]),
  onChange: func,
  cssClass: string,
  isActive: bool,
  initialize: bool,
  targetUrl: string,
  clearState: bool,
  removeOldUploadedFileCallback: func
}

export default Dropzone
